<template>
    <div class="bg-white p-3 rounded-lg shadow-md" data-testid="broadsheet-wizard">
        <div class="flex justify-between items-center mb-3">
            <div>
                <h2 class="text-xl font-bold">New Broadsheet Template</h2>
                <p class="text-xs text-gray-500">Guided setup — answer a few questions and I'll build it for you.</p>
            </div>
            <div class="flex items-center gap-3">
                <button @click="$emit('switch-to-builder')"
                    data-testid="btn-broadsheet-wizard-advanced-builder"
                    class="text-xs text-primary hover:underline">
                    Use advanced builder instead
                </button>
                <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700"
                    data-testid="btn-close-broadsheet-wizard">
                    <i class="pi pi-times text-xl"></i>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <!-- Chat panel -->
            <div class="lg:col-span-3 flex flex-col border border-gray-200 rounded-lg bg-gray-50/50">
                <!-- Progress -->
                <div class="px-4 pt-3">
                    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-primary rounded-full transition-all duration-300"
                            :style="{ width: `${Math.min(100, (progress.answered / progress.total) * 100)}%` }"></div>
                    </div>
                </div>

                <!-- Transcript -->
                <div ref="chatScroll" class="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[20rem] max-h-[26rem]">
                    <template v-for="(entry, index) in transcript" :key="index">
                        <div class="flex">
                            <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm">
                                {{ entry.question }}
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <button @click="jumpBackTo(index)" :disabled="saving"
                                :data-testid="`broadsheet-wizard-answer-${index}`"
                                class="group bg-primary text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm
                                    max-w-[85%] shadow-sm text-left cursor-pointer hover:bg-primary/85 transition-colors"
                                title="Change this answer">
                                {{ entry.answer }}
                                <i class="pi pi-pencil text-[10px] ml-1.5 opacity-0 group-hover:opacity-70 transition-opacity"></i>
                            </button>
                        </div>
                    </template>

                    <!-- Current question -->
                    <div class="flex">
                        <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm"
                            data-testid="broadsheet-wizard-current-question">
                            {{ currentQuestion }}
                        </div>
                    </div>

                    <!-- No result templates notice on the scores step -->
                    <div v-if="currentStep === 'scores' && resultTemplates.length === 0 && !loadingTemplates" class="flex">
                        <div class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 max-w-[85%]">
                            <i class="pi pi-exclamation-triangle mr-1"></i>
                            You haven't created any result templates yet, so I can only offer the standard
                            "total" score. Create a
                            <RouterLink :to="{ path: '/admin/results', query: { tab: 'templates' } }" target="_blank"
                                class="font-semibold underline hover:text-amber-900">result template</RouterLink>
                            first (opens in a new tab) for more options.
                        </div>
                    </div>
                </div>

                <!-- Input area -->
                <div class="border-t border-gray-200 bg-white rounded-b-lg px-4 py-3">
                    <!-- Step: name -->
                    <div v-if="currentStep === 'name'" class="flex gap-2">
                        <InputText v-model="nameInput" class="flex-1" autofocus
                            placeholder="e.g. JSS2 End of Term Broadsheet"
                            data-testid="broadsheet-wizard-input-name" @keyup.enter="answerName" />
                        <Button icon="pi pi-send" :disabled="!nameInput.trim()" @click="answerName"
                            data-testid="broadsheet-wizard-send-name" />
                    </div>

                    <!-- Step: scores -->
                    <div v-else-if="currentStep === 'scores'" class="space-y-2">
                        <div class="flex flex-wrap gap-2 items-center">
                            <button v-for="option in visibleSourceKeyOptions" :key="option.key"
                                @click="toggleSourceKey(option.key)"
                                :data-testid="`broadsheet-wizard-source-${option.key}`"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedSourceKeys.includes(option.key)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'"
                                v-tooltip.top="option.templateNames.length > 0
                                    ? `From: ${option.templateNames.join(', ')}`
                                    : 'Standard key'">
                                <i v-if="selectedSourceKeys.includes(option.key)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                                <span class="font-mono opacity-60 ml-1">({{ option.key }})</span>
                                <span v-if="option.key === 'total'"
                                    class="ml-1.5 text-[9px] uppercase font-bold tracking-wide rounded px-1 py-px"
                                    :class="selectedSourceKeys.includes('total')
                                        ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700'">
                                    Recommended
                                </span>
                            </button>
                            <button v-if="hiddenSourceKeyCount > 0" @click="showAllSourceKeys = true"
                                data-testid="broadsheet-wizard-show-more-keys"
                                class="text-[11px] text-primary hover:underline">
                                + {{ hiddenSourceKeyCount }} more (grades, GPA…)
                            </button>
                        </div>
                        <div v-for="warning in coverageWarnings" :key="warning.key"
                            class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800"
                            data-testid="broadsheet-wizard-coverage-warning">
                            <i class="pi pi-exclamation-triangle mr-1"></i>
                            <strong>{{ warning.label }}</strong> only exists in
                            {{ warning.templateNames.join(', ') }} — classes using your other result
                            templates will show a blank column for it.
                        </div>
                        <div class="flex gap-2">
                            <InputText v-model="customSourceKeyInput" class="flex-1" size="small"
                                placeholder="Add a custom key… e.g. exam"
                                @keyup.enter="addCustomSourceKey" />
                            <Button label="Add" size="small" text :disabled="!customSourceKeyInput.trim()"
                                @click="addCustomSourceKey" />
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                :disabled="selectedSourceKeys.length === 0"
                                data-testid="broadsheet-wizard-confirm-scores" @click="confirmSourceKeys" />
                        </div>
                    </div>

                    <!-- Step: student info -->
                    <div v-else-if="currentStep === 'studentInfo'" class="space-y-2">
                        <div class="flex flex-wrap gap-2">
                            <button v-for="option in studentInfoOptions" :key="option.key"
                                @click="toggleInfoKey(option.key)"
                                :data-testid="`broadsheet-wizard-info-${option.key}`"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedInfoKeys.includes(option.key)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'">
                                <i v-if="selectedInfoKeys.includes(option.key)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                            </button>
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                data-testid="broadsheet-wizard-confirm-info" @click="confirmStudentInfo" />
                        </div>
                    </div>

                    <!-- Step: percentage -->
                    <div v-else-if="currentStep === 'percentage'" class="flex flex-wrap gap-2">
                        <button @click="answerPercentage(true)" data-testid="broadsheet-wizard-chip-percentage-yes" :class="chipClass">
                            Yes, show percentage
                        </button>
                        <button @click="answerPercentage(false)" data-testid="broadsheet-wizard-chip-percentage-no" :class="chipMutedClass">
                            No thanks
                        </button>
                    </div>

                    <!-- Step: position -->
                    <div v-else-if="currentStep === 'position'" class="flex flex-wrap gap-2">
                        <button @click="answerPosition('sum')" data-testid="broadsheet-wizard-chip-position-sum" :class="chipClass">
                            Rank by total score
                        </button>
                        <button @click="answerPosition('average')" data-testid="broadsheet-wizard-chip-position-average" :class="chipClass">
                            Rank by average
                        </button>
                        <button @click="answerPosition('none')" data-testid="broadsheet-wizard-chip-position-none" :class="chipMutedClass">
                            No ranking
                        </button>
                    </div>

                    <!-- Step: headers -->
                    <div v-else-if="currentStep === 'headers'" class="flex flex-wrap gap-2">
                        <button @click="answerHeaders(false)" data-testid="broadsheet-wizard-chip-headers-full" :class="chipClass">
                            Full {{ subjectWord }} names
                        </button>
                        <button @click="answerHeaders(true)" data-testid="broadsheet-wizard-chip-headers-code" :class="chipClass">
                            Short codes
                        </button>
                    </div>

                    <!-- Step: displayOptions -->
                    <div v-else-if="currentStep === 'displayOptions'" class="space-y-3">
                        <div class="space-y-2">
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showUnitInHeaders" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Show credit units in column headers
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showStatusInHeaders" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Show E/C (Elective/Compulsory) status in headers
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showCourseLegend" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Include course legend table (code, title, unit, status)
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showGradeLegend" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Include grading key table (grade, range, point)
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showSignatures" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Include signature section on PDF
                            </label>
                        </div>
                        <div v-if="displayOpts.showSignatures" class="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
                            <p class="text-xs text-gray-500 font-medium">Signature titles (e.g. Head of Department, Dean):</p>
                            <div class="flex flex-wrap gap-1.5">
                                <span v-for="(title, idx) in displayOpts.signatureTitles" :key="idx"
                                    class="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-gray-200 rounded-full">
                                    {{ title }}
                                    <button class="pi pi-times text-[9px] text-gray-400 hover:text-red-500" @click="removeSignatureTitle(idx)"></button>
                                </span>
                            </div>
                            <div class="flex gap-2">
                                <input v-model="displayOpts.signatureInput" type="text" placeholder="Add title..."
                                    class="text-xs px-2 py-1 border border-gray-200 rounded flex-1"
                                    @keyup.enter="addSignatureTitle" />
                                <button @click="addSignatureTitle" class="text-xs px-2 py-1 bg-primary text-white rounded">Add</button>
                            </div>
                        </div>
                        <!-- PDF Header Config -->
                        <div class="mt-3 bg-primary/10 border border-primary/20 rounded-lg p-3 space-y-2">
                            <p class="text-xs text-primary-700 font-medium">PDF Header Overrides (leave blank for defaults)</p>
                            <div class="grid grid-cols-2 gap-2">
                                <div>
                                    <label class="block text-[10px] text-gray-500">School Name Override</label>
                                    <input v-model="displayOpts.pdfSchoolName" type="text" placeholder="e.g. THE REDEEMED COLLEGE OF MISSIONS..."
                                        class="text-xs px-2 py-1 border border-gray-200 rounded w-full" />
                                </div>
                                <div>
                                    <label class="block text-[10px] text-gray-500">Report Title Override</label>
                                    <input v-model="displayOpts.pdfReportTitle" type="text" placeholder="e.g. BROADSHEET FOR PRESENTATION..."
                                        class="text-xs px-2 py-1 border border-gray-200 rounded w-full" />
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>
                                    <label class="block text-[10px] text-gray-500">Semester Display</label>
                                    <select v-model="displayOpts.pdfSemesterFormat" class="text-xs px-2 py-1 border border-gray-200 rounded w-full">
                                        <option value="number">Number only (1, 2)</option>
                                        <option value="full">Full name (1st Semester)</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-[10px] text-gray-500">Logo Layout</label>
                                    <select v-model="displayOpts.pdfLogoPosition" class="text-xs px-2 py-1 border border-gray-200 rounded w-full">
                                        <option value="left">School logo left, Affiliate right</option>
                                        <option value="right">Affiliate left, School logo right</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label class="block text-[10px] text-gray-500">Level Display Map (for Diploma/Certificate — e.g. 100→DIPLOMA ONE)</label>
                                <div class="flex flex-wrap gap-1.5 mb-1">
                                    <span v-for="(display, lvl) in displayOpts.pdfLevelDisplayMap" :key="lvl"
                                        class="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-white border border-gray-200 rounded-full">
                                        {{ lvl }} → {{ display }}
                                        <button class="pi pi-times text-[8px] text-gray-400 hover:text-red-500" @click="delete displayOpts.pdfLevelDisplayMap[lvl]"></button>
                                    </span>
                                </div>
                                <div class="flex gap-1">
                                    <input v-model="displayOpts.pdfLevelMapInput" type="text" placeholder="100:DIPLOMA ONE"
                                        class="text-xs px-2 py-1 border border-gray-200 rounded flex-1"
                                        @keyup.enter="addLevelMapping" />
                                    <button @click="addLevelMapping"
                                        class="text-[10px] px-2 py-1 bg-primary text-white rounded">Add</button>
                                </div>
                            </div>
                        </div>
                        <button @click="confirmDisplayOptions" :class="chipClass" class="mt-2"
                            data-testid="broadsheet-wizard-confirm-display-options">
                            Continue
                        </button>
                    </div>

                    <!-- Step: review -->
                    <div v-else-if="currentStep === 'review'" class="space-y-3">
                        <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                            <p><span class="text-gray-500">Broadsheet:</span> <strong>{{ answers.name }}</strong></p>
                            <p>
                                <span class="text-gray-500">Columns:</span>
                                {{ reviewColumns.map((c) => c.label).join(' · ') }}
                            </p>
                            <p>
                                <span class="text-gray-500">Scores per {{ subjectWord }}:</span>
                                {{ answers.sourceKeys.map((s) => s.label).join(', ') }}
                            </p>
                        </div>

                        <div class="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 text-xs text-primary-700">
                            <i class="pi pi-info-circle mr-1"></i>
                            One template works for every class — after creating, click its
                            <strong>▶ Generate Report</strong> button in the list to run it for any class and term.
                            Need fine-tuning later? <strong>Edit</strong> opens the advanced builder.
                        </div>

                        <div class="flex justify-end">
                            <Button :label="saving ? 'Creating…' : 'Create Broadsheet Template'" icon="pi pi-check"
                                :loading="saving" data-testid="btn-broadsheet-wizard-create" @click="saveTemplate" />
                        </div>
                    </div>

                    <!-- Back -->
                    <div v-if="transcript.length > 0 && !saving" class="mt-2 flex items-center justify-between">
                        <button @click="goBack" class="text-[11px] text-gray-400 hover:text-gray-600"
                            data-testid="broadsheet-wizard-back">
                            <i class="pi pi-undo mr-1"></i>Back to previous question
                        </button>
                        <span class="text-[11px] text-gray-300">or click any answer above to change it</span>
                    </div>
                </div>
            </div>

            <!-- Live preview -->
            <div class="lg:col-span-2">
                <BroadsheetWizardPreview :answers="previewAnswers" :rows="previewRows" :isTertiary="isTertiary" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useBroadsheetWizard } from '@/composables/useBroadsheetWizard';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import BroadsheetWizardPreview from './BroadsheetWizardPreview.vue';

const emit = defineEmits(['close', 'saved', 'switch-to-builder']);

const { isTertiary } = useSchoolFeatures();

const {
  answers,
  transcript,
  saving,
  nameInput,
  selectedSourceKeys,
  customSourceKeyInput,
  selectedInfoKeys,
  currentStep,
  currentQuestion,
  progress,
  loadingTemplates,
  resultTemplates,
  visibleSourceKeyOptions,
  hiddenSourceKeyCount,
  showAllSourceKeys,
  coverageWarnings,
  studentInfoOptions,
  subjectWord,
  previewAnswers,
  previewRows,
  reviewColumns,
  answerName,
  toggleSourceKey,
  addCustomSourceKey,
  confirmSourceKeys,
  toggleInfoKey,
  confirmStudentInfo,
  answerPercentage,
  answerPosition,
  answerHeaders,
  displayOpts,
  addSignatureTitle,
  removeSignatureTitle,
  addLevelMapping,
  confirmDisplayOptions,
  goBack,
  jumpBackTo,
  saveTemplate,
} = useBroadsheetWizard(emit, isTertiary);

const chipClass = 'text-sm px-3 py-1.5 rounded-full border border-primary/40 text-primary bg-primary/5 hover:bg-primary hover:text-white transition-colors';
const chipMutedClass = 'text-sm px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 transition-colors';

// Keep the newest message in view as the conversation grows.
const chatScroll = ref<HTMLElement | null>(null);
watch(
  () => [transcript.value.length, currentStep.value],
  async() => {
    await nextTick();
    chatScroll.value?.scrollTo({ top: chatScroll.value.scrollHeight, behavior: 'smooth' });
  },
);
</script>
